import React, { useContext, useMemo, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { collection, getDocs, doc, writeBatch, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import { useAgenda } from '../hooks/useAgenda';
import { useAgendas } from '../hooks/useAgendas';
import { useCustomStatuses, AVAILABLE_COLORS } from '../hooks/useCustomStatuses';
import { downloadCsv } from '../utils/downloadCsv';
import { isValidBusinessEmail, sanitizeEmail } from '../utils/email';
import { findDuplicates, describeDuplicateReasons } from '../utils/duplicateDetection';
import {
    getCachedAllContacts,
    setCachedAllContacts,
    invalidateContactsCache,
    patchCachedContacts,
    updateCachedContacts,
    removeCachedContacts,
} from '../utils/contactsCache';

const DEFAULT_AGENDA_COLUMNS = {
    agenda: true,
    name: true,
    address: true,
    phone: true,
    website: true,
    email: true,
    notes: true,
    status: true,
    acciones: true
};

// El caché de "Todos los contactos" se maneja en `../utils/contactsCache`.
// Acá solo exponemos un alias local para no tocar todos los call-sites.
const invalidateAllContactsCache = invalidateContactsCache;

// Lee los contactos de TODAS las agendas desde Firestore (sin caché).
// Uso interno: si hay caché fresco preferimos usarlo; si no, esto repuebla.
async function fetchAllContactsFromFirestore(user, agendas) {
    const allContacts = [];
    for (const agenda of agendas) {
        const agendaRef = agenda.id === 'main'
            ? collection(db, 'users', user.uid, 'agenda')
            : collection(db, 'users', user.uid, 'agendas', agenda.id, 'contacts');
        const snapshot = await getDocs(agendaRef);
        snapshot.docs.forEach((docSnap) => {
            allContacts.push({
                ...docSnap.data(),
                id: docSnap.id,
                agendaId: agenda.id,
                agendaName: agenda.name,
            });
        });
    }
    return allContacts;
}

// Devuelve todos los contactos del usuario usando el caché compartido.
// Si el caché está vigente no hace ninguna petición a Firestore.
async function getAllContactsCachedOrFetch(user, agendas) {
    const cached = getCachedAllContacts(user, agendas);
    if (cached) return cached;
    const data = await fetchAllContactsFromFirestore(user, agendas);
    setCachedAllContacts(user, agendas, data);
    return data;
}

// Deja solo los dígitos de un string. Útil para matchear teléfonos sin importar
// espacios, guiones, paréntesis o prefijos internacionales.
const digitsOnly = (value) => (value || '').toString().replace(/\D/g, '');

// Compara dos cadenas de dígitos de teléfono aceptando:
//  - que una contenga a la otra (manejan espacios/guiones/paréntesis/prefijos),
//  - que compartan un sufijo suficientemente largo, para el caso en que el
//    usuario escribe con prefijo internacional (ej. "+54 9 11 3576-8851")
//    pero el teléfono guardado está en formato local ("11 3576-8851"), o
//    viceversa.
function phoneDigitsMatch(termDigits, phoneDigits) {
    if (!termDigits || !phoneDigits) return false;
    if (phoneDigits.includes(termDigits)) return true;
    if (termDigits.includes(phoneDigits) && phoneDigits.length >= 7) return true;

    // Match por sufijo: los números telefónicos típicamente coinciden en los
    // últimos 7-8 dígitos más allá del prefijo internacional/nacional.
    const minLen = Math.min(termDigits.length, phoneDigits.length);
    if (minLen >= 7) {
        const tail = Math.min(minLen, 10);
        if (termDigits.slice(-tail) === phoneDigits.slice(-tail)) return true;
    }
    return false;
}

// Decide si un item de agenda hace match con el término de búsqueda.
// - Busca texto en nombre / email / dirección / teléfono / whatsapp / ciudad / categoría.
// - Si el término contiene al menos 3 dígitos, también compara teléfonos y
//   whatsapp normalizados (sin espacios, guiones, paréntesis ni prefijos).
function itemMatchesSearch(item, searchTerm) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;

    const textMatch = (
        (item.name || '').toLowerCase().includes(term) ||
        (item.email || '').toLowerCase().includes(term) ||
        (item.phone || '').toLowerCase().includes(term) ||
        (item.whatsapp || '').toLowerCase().includes(term) ||
        (item.address || '').toLowerCase().includes(term) ||
        (item.city || '').toLowerCase().includes(term) ||
        (item.category || '').toLowerCase().includes(term)
    );
    if (textMatch) return true;

    const termDigits = digitsOnly(term);
    if (termDigits.length >= 3) {
        const phoneDigits = digitsOnly(item.phone);
        const whatsappDigits = digitsOnly(item.whatsapp);
        if (phoneDigitsMatch(termDigits, phoneDigits)) return true;
        if (phoneDigitsMatch(termDigits, whatsappDigits)) return true;
    }
    return false;
}

const Dashboard = forwardRef((props, ref) => {
    const { user, plan, signInWithGoogle } = useContext(AuthContext);
    const { agendas, counts, createAgenda, updateAgendaName, updateCounts } = useAgendas();
    const { allStatuses, createStatus, deleteStatus, getStatusById } = useCustomStatuses();
    const [selectedAgendaId, setSelectedAgendaId] = useState('main');

    const { items, updateAgendaItem, canAddMore, maxFree, moveContactsToAgenda, deleteContacts, createManualContact, loading, filterOptions } = useAgenda(selectedAgendaId);
    const [allAgendaItems, setAllAgendaItems] = useState([]);
    const [cityFilter, setCityFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('complete-desc');
    const [fieldFilters, setFieldFilters] = useState({
        whatsapp: false,
        phone: false,
        email: false,
        website: false,
        duplicates: false
    });
    const [editedItems, setEditedItems] = useState({});
    const [editingRowId, setEditingRowId] = useState(null);
    const [flashEffect, setFlashEffect] = useState({ placeId: null, type: null }); // 'save' or 'cancel'
    const [frozenOrder, setFrozenOrder] = useState(null); // Orden congelado durante edición
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20); // Valor predeterminado cambiado a 20
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [previousColumnsConfig, setPreviousColumnsConfig] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        if (typeof window === 'undefined') return { ...DEFAULT_AGENDA_COLUMNS };
        try {
            const stored = JSON.parse(localStorage.getItem('agenda_visible_columns_v1') || '{}');
            // Si hay configuración guardada, usarla
            if (Object.keys(stored).length > 0) {
                return { ...DEFAULT_AGENDA_COLUMNS, ...stored };
            }
            return { ...DEFAULT_AGENDA_COLUMNS };
        } catch {
            return { ...DEFAULT_AGENDA_COLUMNS };
        }
    });
    const [selectedContacts, setSelectedContacts] = useState(new Set());
    const [showCreateAgenda, setShowCreateAgenda] = useState(false);
    const [newAgendaName, setNewAgendaName] = useState('');
    const [editingAgendaId, setEditingAgendaId] = useState(null);
    const [editingAgendaName, setEditingAgendaName] = useState('');
    const [showCreateContact, setShowCreateContact] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkLoadingMessage, setBulkLoadingMessage] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [createContactLoading, setCreateContactLoading] = useState(false);
    const [allContactsLoading, setAllContactsLoading] = useState(false);
    const [deletingContacts, setDeletingContacts] = useState(new Set());
    const [movingContacts, setMovingContacts] = useState(new Set());
    const [toast, setToast] = useState(null); // { type: 'success' | 'error' | 'info', message: string }
    const MAX_NOTES_LENGTH = 1000;

    // Funciones helper para WhatsApp
    const formatWhatsappNumber = (phone) => {
        let num = phone.replace(/\D/g, '');
        if (num.startsWith('0')) num = num.slice(1);
        if (num.length <= 10) num = '54' + num;
        return num;
    };

    const isValidWhatsappNumber = (phone) => {
        const num = phone.replace(/\D/g, '');
        return num.length >= 10 && num.length <= 13;
    };

    const [newContact, setNewContact] = useState({
        name: '',
        address: '',
        phone: '',
        website: '',
        email: '',
        city: '',
        category: '',
        notes: '',
        status: 'pendiente',
    });

    useEffect(() => {
        try {
            localStorage.setItem('agenda_visible_columns_v1', JSON.stringify(visibleColumns));
        } catch {
            // Silencioso: localStorage puede no estar disponible
        }
    }, [visibleColumns]);


    const isColumnVisible = (columnId) => {
        return visibleColumns[columnId] !== false;
    };

    const toggleColumnVisibility = (columnId) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [columnId]: !prev[columnId]
        }));
    };

    const resetColumnVisibility = () => {
        setVisibleColumns({ ...DEFAULT_AGENDA_COLUMNS });
        setPreviousColumnsConfig(null);
    };

    const isQuickViewActive = useMemo(() => {
        const quickViewConfig = {
            agenda: false,
            name: true,
            address: false,
            phone: true,
            website: false,
            email: false,
            notes: false,
            status: false,
            acciones: false
        };
        return JSON.stringify(visibleColumns) === JSON.stringify(quickViewConfig);
    }, [visibleColumns]);

    const toggleQuickView = () => {
        if (isQuickViewActive) {
            // Restaurar configuración anterior o usar defaults
            if (previousColumnsConfig) {
                setVisibleColumns({ ...previousColumnsConfig });
                setPreviousColumnsConfig(null);
            } else {
                setVisibleColumns({ ...DEFAULT_AGENDA_COLUMNS });
            }
        } else {
            // Guardar configuración actual y aplicar vista rápida
            setPreviousColumnsConfig({ ...visibleColumns });
            setVisibleColumns({
                agenda: false,
                name: true,
                address: false,
                phone: true,
                website: false,
                email: false,
                notes: false,
                status: false,
                acciones: false
            });
        }
    };

    const columnOptions = useMemo(() => {
        const options = [
            { id: 'name', label: 'Nombre' },
            { id: 'address', label: 'Dirección' },
            { id: 'phone', label: 'Teléfono' },
            { id: 'website', label: 'Website' },
            { id: 'email', label: 'Email' },
            { id: 'notes', label: 'Notas' },
            { id: 'status', label: 'Estado' },
            { id: 'acciones', label: 'Acciones' }
        ];
        if (selectedAgendaId === 'all') {
            options.unshift({ id: 'agenda', label: 'Agenda' });
        }
        return options;
    }, [selectedAgendaId]);

    // Modal de notas
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesModalItemId, setNotesModalItemId] = useState(null);
    const [notesModalText, setNotesModalText] = useState('');
    const [notesModalTitle, setNotesModalTitle] = useState('');

    // Modal de cambio masivo de estado
    const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('pendiente');

    // Modal de notas masivas
    const [showBulkNotesModal, setShowBulkNotesModal] = useState(false);
    const [bulkNotesText, setBulkNotesText] = useState('');

    // Modal de WhatsApp masivo
    const [showBulkWhatsAppModal, setShowBulkWhatsAppModal] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState('');
    const [showWhatsappLinksModal, setShowWhatsappLinksModal] = useState(false);
    const [whatsappContacts, setWhatsappContacts] = useState([]);
    const [sentContacts, setSentContacts] = useState(new Set());
    const [whatsappNotAvailable, setWhatsappNotAvailable] = useState(new Set());

    // Modal de gestión de estados personalizados
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusColor, setNewStatusColor] = useState('#007bff');
    const [newStatusEmoji, setNewStatusEmoji] = useState('📌');
    const [showManageStatusModal, setShowManageStatusModal] = useState(false);
    const [pendingStatusContactId, setPendingStatusContactId] = useState(null); // Para aplicar estado después de crear

    // Bloquear scroll cuando hay modales abiertos
    useEffect(() => {
        const isAnyModalOpen = showNotesModal || showBulkStatusModal || showBulkNotesModal ||
            showBulkWhatsAppModal || showWhatsappLinksModal || showManageStatusModal;

        if (isAnyModalOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showNotesModal, showBulkStatusModal, showBulkNotesModal, showBulkWhatsAppModal, showWhatsappLinksModal, showManageStatusModal]);

    // Cargar todos los contactos cuando se selecciona 'all' (con caché para no repetir peticiones)
    useEffect(() => {
        if (selectedAgendaId !== 'all' || !user) {
            setAllAgendaItems([]);
            setAllContactsLoading(false);
            return;
        }

        // Sirve desde caché (memoria o sessionStorage) si está vigente:
        // evita las N `getDocs` (una por agenda) en la mayoría de los casos.
        const cached = getCachedAllContacts(user, agendas);
        if (cached) {
            setAllAgendaItems(cached);
            setAllContactsLoading(false);
            return;
        }

        const loadAllContacts = async () => {
            setAllContactsLoading(true);
            try {
                const allContacts = await fetchAllContactsFromFirestore(user, agendas);
                setAllAgendaItems(allContacts);
                setCachedAllContacts(user, agendas, allContacts);
            } catch (err) {
                /* noop */
            } finally {
                setAllContactsLoading(false);
            }
        };

        loadAllContacts();
    }, [selectedAgendaId, agendas, user]);

    // Limpiar allAgendaItems inmediatamente al cambiar a una agenda individual
    useEffect(() => {
        if (selectedAgendaId !== 'all') {
            setAllAgendaItems([]);
        }
    }, [selectedAgendaId]);

    // Los filtros con conteos ahora vienen del hook useAgenda
    const displayItems = selectedAgendaId === 'all' ? allAgendaItems : items;

    // Detección de duplicados sobre TODO el dataset visible (antes de filtros),
    // para que el badge y el contador sean consistentes independientemente del
    // filtro activo. Memoizado para no recalcular en cada render.
    const duplicatesMap = useMemo(
        () => findDuplicates(displayItems),
        [displayItems]
    );
    const duplicatesCount = duplicatesMap.size;

    // Calcular filterOptions dinámicamente para 'all' o usar las del hook
    const dynamicFilterOptions = useMemo(() => {
        if (selectedAgendaId !== 'all') {
            return filterOptions;
        }

        // Calcular opciones de filtro desde allAgendaItems
        const cityCounts = {};
        const categoryCounts = {};
        const statusCounts = {};

        allAgendaItems.forEach((item) => {
            if (item.city) {
                cityCounts[item.city] = (cityCounts[item.city] || 0) + 1;
            }
            if (item.category) {
                categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
            }
            const status = item.status || 'pendiente';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        return {
            cities: Object.keys(cityCounts).sort().map(city => ({
                value: city,
                label: `${city} (${cityCounts[city]})`,
                count: cityCounts[city]
            })),
            categories: Object.keys(categoryCounts).sort().map(category => ({
                value: category,
                label: `${category} (${categoryCounts[category]})`,
                count: categoryCounts[category]
            })),
            statuses: Object.keys(statusCounts).sort().map(status => ({
                value: status,
                label: `${status} (${statusCounts[status]})`,
                count: statusCounts[status]
            }))
        };
    }, [selectedAgendaId, filterOptions, allAgendaItems]);

    const filteredItems = useMemo(
        () => {
            // Función para contar campos completos
            const countFilledFields = (item) => {
                let count = 0;
                if (item.name && item.name.trim()) count++;
                if (item.address && item.address.trim()) count++;
                if (item.phone && item.phone.trim()) count++;
                if (isValidBusinessEmail(item.email)) count++;
                if (item.website && item.website.trim()) count++;
                if (item.whatsapp && item.whatsapp.trim()) count++;
                if (item.rating) count++;
                if (item.reviews) count++;
                if (item.city && item.city.trim()) count++;
                if (item.category && item.category.trim()) count++;
                if (item.notes && item.notes.trim()) count++;
                return count;
            };

            let items = displayItems
                .filter((item) => (cityFilter === 'all' || item.city === cityFilter))
                .filter((item) => (categoryFilter === 'all' || item.category === categoryFilter))
                .filter((item) => (statusFilter === 'all' || (item.status || 'pendiente') === statusFilter))
                .filter((item) => itemMatchesSearch(item, searchTerm))
                .filter((item) => {
                    // Filtros de campos disponibles
                    if (fieldFilters.whatsapp && (!item.phone || !isValidWhatsappNumber(item.phone) || item.whatsappNotAvailable)) {
                        return false;
                    }
                    if (fieldFilters.phone && (!item.phone || !item.phone.trim())) {
                        return false;
                    }
                    if (fieldFilters.email && !isValidBusinessEmail(item.email)) {
                        return false;
                    }
                    if (fieldFilters.website && (!item.website || !item.website.trim())) {
                        return false;
                    }
                    if (fieldFilters.duplicates && !duplicatesMap.has(item.id || item.placeId)) {
                        return false;
                    }
                    return true;
                });

            // Si hay un orden congelado, usarlo directamente
            if (frozenOrder) {
                // Crear un mapa de IDs a items actuales
                const itemsMap = new Map(items.map(item => [(item.placeId || item.id), item]));
                // Reconstruir el orden usando el orden congelado
                const orderedItems = frozenOrder
                    .map(id => itemsMap.get(id))
                    .filter(Boolean); // Filtrar items que ya no existen
                // Agregar items nuevos que no estaban en el orden congelado
                const newItems = items.filter(item => !frozenOrder.includes(item.placeId || item.id));
                return [...orderedItems, ...newItems];
            }
            
            // Si no hay orden congelado pero hay edición/flash activo, aplicar filtros pero NO ordenar
            const isEditingOrFlashing = editingRowId || flashEffect.placeId;
            
            if (!isEditingOrFlashing) {
                items = items.sort((a, b) => {
                    switch (sortBy) {
                        case 'whatsapp-complete':
                            const whatsappA = a.whatsappNotAvailable ? 1 : 0;
                            const whatsappB = b.whatsappNotAvailable ? 1 : 0;
                            if (whatsappA !== whatsappB) return whatsappA - whatsappB;
                            const fieldsA = countFilledFields(a);
                            const fieldsB = countFilledFields(b);
                            if (fieldsB !== fieldsA) return fieldsB - fieldsA;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'name-asc':
                            return (a.name || '').localeCompare(b.name || '');
                        case 'name-desc':
                            return (b.name || '').localeCompare(a.name || '');
                        case 'city':
                            const cityComp = (a.city || '').localeCompare(b.city || '');
                            if (cityComp !== 0) return cityComp;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'category':
                            const catComp = (a.category || '').localeCompare(b.category || '');
                            if (catComp !== 0) return catComp;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'complete-desc':
                            const fieldsCompA = countFilledFields(a);
                            const fieldsCompB = countFilledFields(b);
                            if (fieldsCompB !== fieldsCompA) return fieldsCompB - fieldsCompA;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'complete-asc':
                            const fieldsCompA2 = countFilledFields(a);
                            const fieldsCompB2 = countFilledFields(b);
                            if (fieldsCompA2 !== fieldsCompB2) return fieldsCompA2 - fieldsCompB2;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'whatsapp-available':
                            const wpA = a.whatsappNotAvailable ? 1 : 0;
                            const wpB = b.whatsappNotAvailable ? 1 : 0;
                            if (wpA !== wpB) return wpA - wpB;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'date-newest':
                            const dateA = a.createdAt?.seconds || 0;
                            const dateB = b.createdAt?.seconds || 0;
                            if (dateB !== dateA) return dateB - dateA;
                            return (a.name || '').localeCompare(b.name || '');
                        case 'date-oldest':
                            const dateA2 = a.createdAt?.seconds || 0;
                            const dateB2 = b.createdAt?.seconds || 0;
                            if (dateA2 !== dateB2) return dateA2 - dateB2;
                            return (a.name || '').localeCompare(b.name || '');
                        default:
                            return 0;
                    }
                });
            }
            return items;
        },
        [displayItems, cityFilter, categoryFilter, statusFilter, searchTerm, sortBy, fieldFilters, editingRowId, flashEffect, frozenOrder, duplicatesMap]
    );

    // Congelar el orden cuando se activa la edición
    useEffect(() => {
        if (editingRowId && !frozenOrder) {
            // Calcular el orden filtrado (sin ordenar) y guardar los IDs
            const countFilledFields = (item) => {
                let count = 0;
                if (item.name && item.name.trim()) count++;
                if (item.address && item.address.trim()) count++;
                if (item.phone && item.phone.trim()) count++;
                if (isValidBusinessEmail(item.email)) count++;
                if (item.website && item.website.trim()) count++;
                if (item.whatsapp && item.whatsapp.trim()) count++;
                if (item.rating) count++;
                if (item.reviews) count++;
                if (item.city && item.city.trim()) count++;
                if (item.category && item.category.trim()) count++;
                if (item.notes && item.notes.trim()) count++;
                return count;
            };

            let items = displayItems
                .filter((item) => (cityFilter === 'all' || item.city === cityFilter))
                .filter((item) => (categoryFilter === 'all' || item.category === categoryFilter))
                .filter((item) => (statusFilter === 'all' || (item.status || 'pendiente') === statusFilter))
                .filter((item) => itemMatchesSearch(item, searchTerm))
                .filter((item) => {
                    if (fieldFilters.whatsapp && (!item.phone || !isValidWhatsappNumber(item.phone) || item.whatsappNotAvailable)) {
                        return false;
                    }
                    if (fieldFilters.phone && (!item.phone || !item.phone.trim())) {
                        return false;
                    }
                    if (fieldFilters.email && !isValidBusinessEmail(item.email)) {
                        return false;
                    }
                    if (fieldFilters.website && (!item.website || !item.website.trim())) {
                        return false;
                    }
                    if (fieldFilters.duplicates && !duplicatesMap.has(item.id || item.placeId)) {
                        return false;
                    }
                    return true;
                });

            // Aplicar el ordenamiento actual para obtener el orden "correcto" antes de congelar
            items = items.sort((a, b) => {
                switch (sortBy) {
                    case 'whatsapp-complete':
                        const whatsappA = a.whatsappNotAvailable ? 1 : 0;
                        const whatsappB = b.whatsappNotAvailable ? 1 : 0;
                        if (whatsappA !== whatsappB) return whatsappA - whatsappB;
                        const fieldsA = countFilledFields(a);
                        const fieldsB = countFilledFields(b);
                        if (fieldsB !== fieldsA) return fieldsB - fieldsA;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'name-asc':
                        return (a.name || '').localeCompare(b.name || '');
                    case 'name-desc':
                        return (b.name || '').localeCompare(a.name || '');
                    case 'city':
                        const cityComp = (a.city || '').localeCompare(b.city || '');
                        if (cityComp !== 0) return cityComp;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'category':
                        const catComp = (a.category || '').localeCompare(b.category || '');
                        if (catComp !== 0) return catComp;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'complete-desc':
                        const fieldsCompA = countFilledFields(a);
                        const fieldsCompB = countFilledFields(b);
                        if (fieldsCompB !== fieldsCompA) return fieldsCompB - fieldsCompA;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'complete-asc':
                        const fieldsCompA2 = countFilledFields(a);
                        const fieldsCompB2 = countFilledFields(b);
                        if (fieldsCompA2 !== fieldsCompB2) return fieldsCompA2 - fieldsCompB2;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'whatsapp-available':
                        const wpA = a.whatsappNotAvailable ? 1 : 0;
                        const wpB = b.whatsappNotAvailable ? 1 : 0;
                        if (wpA !== wpB) return wpA - wpB;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'date-newest':
                        const dateA = a.createdAt?.seconds || 0;
                        const dateB = b.createdAt?.seconds || 0;
                        if (dateB !== dateA) return dateB - dateA;
                        return (a.name || '').localeCompare(b.name || '');
                    case 'date-oldest':
                        const dateA2 = a.createdAt?.seconds || 0;
                        const dateB2 = b.createdAt?.seconds || 0;
                        if (dateA2 !== dateB2) return dateA2 - dateB2;
                        return (a.name || '').localeCompare(b.name || '');
                    default:
                        return 0;
                }
            });

            // Guardar los IDs en el orden actual
            const orderIds = items.map(item => item.placeId || item.id);
            setFrozenOrder(orderIds);
        } else if (!editingRowId && !flashEffect.placeId && frozenOrder) {
            // Descongelar cuando se termina la edición y el flash
            setFrozenOrder(null);
        }
    }, [editingRowId, flashEffect.placeId, frozenOrder, displayItems, cityFilter, categoryFilter, statusFilter, searchTerm, sortBy, fieldFilters, duplicatesMap]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);
    const visibleColumnCount = (() => {
        const baseColumns = ['name', 'address', 'phone', 'website', 'email', 'notes', 'status', 'acciones'];
        let count = baseColumns.reduce((acc, id) => acc + (isColumnVisible(id) ? 1 : 0), 0);
        if (selectedAgendaId === 'all' && isColumnVisible('agenda')) {
            count += 1;
        }
        return count + 1; // Selección (checkbox)
    })();

    // Función auxiliar para obtener el ID único de un item
    const getItemId = (item) => {
        return item.placeId || item.id;
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSelectedContacts(new Set()); // Deseleccionar al cambiar de página
        }
    };

    const handleSelectAll = () => {
        // Verificar si todos los items de la página actual están seleccionados
        const allPageItemsSelected = paginatedItems.every(item => selectedContacts.has(getItemId(item)));

        if (allPageItemsSelected && paginatedItems.length > 0) {
            // Deseleccionar todos los items de la página actual
            const newSelected = new Set(selectedContacts);
            paginatedItems.forEach(item => newSelected.delete(getItemId(item)));
            setSelectedContacts(newSelected);
        } else {
            // Seleccionar todos los items de la página actual
            const newSelected = new Set(selectedContacts);
            paginatedItems.forEach(item => newSelected.add(getItemId(item)));
            setSelectedContacts(newSelected);
        }
    };

    const handleSelectContact = (placeId) => {
        const newSelected = new Set(selectedContacts);
        if (newSelected.has(placeId)) {
            newSelected.delete(placeId);
        } else {
            newSelected.add(placeId);
        }
        setSelectedContacts(newSelected);
    };

    const handleCloseBulkActions = () => {
        setSelectedContacts(new Set());
    };

    const handleDeleteSelected = async () => {
        if (selectedContacts.size === 0) return;
        const confirmDelete = window.confirm(`¿Estás seguro de que deseas eliminar ${selectedContacts.size} contacto(s)? Esta acción no se puede deshacer.`);
        if (!confirmDelete) return;

        const contactsToDelete = Array.from(selectedContacts);
        const count = contactsToDelete.length;

        // Marcar contactos como eliminándose para animación
        setDeletingContacts(new Set(contactsToDelete));

        // Esperar un poco para que se vea la animación
        setTimeout(async () => {
            setBulkLoading(true);
            setBulkLoadingMessage(`Eliminando ${count} contacto(s)...`);
            try {
                // Si estamos en "Todas las agendas", borrar de las agendas correspondientes
                if (selectedAgendaId === 'all') {
                    const batch = writeBatch(db);

                    contactsToDelete.forEach((contactId) => {
                        // Buscar el contacto en allAgendaItems para saber su agendaId
                        const contact = allAgendaItems.find(item => item.id === contactId);
                        if (contact) {
                            let docRef;
                            if (contact.agendaId === 'main') {
                                docRef = doc(db, 'users', user.uid, 'agenda', contactId);
                            } else {
                                docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contactId);
                            }
                            batch.delete(docRef);
                        }
                    });

                    await batch.commit();

                    // Actualizamos el estado local en vez de re-fetchear desde
                    // Firestore (ya sabemos exactamente qué IDs se borraron).
                    const deletedSet = new Set(contactsToDelete);
                    setAllAgendaItems((prev) => {
                        const updated = prev.filter((item) => !deletedSet.has(item.id));
                        // Mantenemos el caché alineado con el estado local.
                        setCachedAllContacts(user, agendas, updated);
                        return updated;
                    });
                } else {
                    // Borrado normal desde una agenda específica
                    await deleteContacts(contactsToDelete);
                    removeCachedContacts(user, agendas, contactsToDelete);
                }

                setSelectedContacts(new Set());
                await updateCounts();
                showToast('success', `${count} contacto(s) eliminado(s) exitosamente`);
            } catch (err) {
                showToast('error', 'Error al eliminar contactos');
                // Revertir en caso de error
                setDeletingContacts(new Set());
            } finally {
                setBulkLoading(false);
                setBulkLoadingMessage('');
                setDeletingContacts(new Set());
            }
        }, 300);
    };

    const handleMoveSelected = async (targetAgendaId) => {
        if (!targetAgendaId || targetAgendaId === '' || selectedContacts.size === 0 || targetAgendaId === selectedAgendaId) return;

        const contactsToMove = Array.from(selectedContacts);
        const count = contactsToMove.length;
        const targetAgenda = agendas.find(a => a.id === targetAgendaId);

        // Marcar contactos como moviéndose para animación
        setMovingContacts(new Set(contactsToMove));

        // Esperar un poco para que se vea la animación
        setTimeout(async () => {
            setBulkLoading(true);
            setBulkLoadingMessage(`Moviendo ${count} contacto(s)...`);
            try {
                await moveContactsToAgenda(contactsToMove, targetAgendaId);
                // Parcheamos el caché con el nuevo agendaId/agendaName, así
                // no hace falta refetchear toda la base.
                patchCachedContacts(user, agendas, contactsToMove, {
                    agendaId: targetAgendaId,
                    agendaName: targetAgenda?.name || '',
                });
                setSelectedContacts(new Set());
                await updateCounts();
                showToast('success', `${count} contacto(s) movido(s) a "${targetAgenda?.name}"`);
            } catch (err) {
                showToast('error', 'Error al mover los contactos');
                // Revertir en caso de error
                setMovingContacts(new Set());
            } finally {
                setBulkLoading(false);
                setBulkLoadingMessage('');
                setMovingContacts(new Set());
            }
        }, 300);
    };

    const handleBulkStatusChange = async () => {
        if (selectedContacts.size === 0) return;
        const count = selectedContacts.size;
        const statusName = getStatusById(bulkStatus)?.name || bulkStatus;

        setBulkLoading(true);
        setBulkLoadingMessage(`Actualizando estado de ${count} contacto(s)...`);
        try {
            if (selectedAgendaId === 'all') {
                // En modo "all", actualizar cada contacto en su agenda correspondiente
                await Promise.all(
                    Array.from(selectedContacts).map(async (selectedId) => {
                        const contact = allAgendaItems.find(item =>
                            getItemId(item) === selectedId
                        );
                        if (!contact) return;

                        let docRef;
                        if (contact.agendaId === 'main') {
                            docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                        } else {
                            docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                        }
                        await updateDoc(docRef, {
                            status: bulkStatus,
                            updatedAt: serverTimestamp()
                        });
                    })
                );
            } else {
                // En modo normal, usar updateAgendaItem
                await Promise.all(
                    Array.from(selectedContacts).map(placeId =>
                        updateAgendaItem(placeId, { status: bulkStatus })
                    )
                );
            }
            setShowBulkStatusModal(false);
            const affected = Array.from(selectedContacts);
            setSelectedContacts(new Set());
            // Patcheamos el caché si está cargado; si no, es no-op.
            patchCachedContacts(user, agendas, affected, { status: bulkStatus });
            showToast('success', `${count} contacto(s) actualizado(s) a "${statusName}"`);
        } catch (err) {
            showToast('error', 'Error al cambiar el estado de los contactos');
        } finally {
            setBulkLoading(false);
            setBulkLoadingMessage('');
        }
    };

    const handleBulkNotesAdd = async () => {
        if (selectedContacts.size === 0 || !bulkNotesText.trim()) return;
        const count = selectedContacts.size;

        setBulkLoading(true);
        setBulkLoadingMessage(`Agregando notas a ${count} contacto(s)...`);
        try {
            // Mapa id → nuevas notas, para patchear el caché al final con la
            // data exacta que quedó en Firestore (notas concatenadas).
            const newNotesById = new Map();

            if (selectedAgendaId === 'all') {
                // En modo "all", actualizar cada contacto en su agenda correspondiente
                await Promise.all(
                    Array.from(selectedContacts).map(async (selectedId) => {
                        const contact = allAgendaItems.find(item =>
                            getItemId(item) === selectedId
                        );
                        if (!contact) return;

                        const currentNotes = contact.notes || '';
                        const newNotes = currentNotes ? `${currentNotes}\n${bulkNotesText}` : bulkNotesText;

                        let docRef;
                        if (contact.agendaId === 'main') {
                            docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                        } else {
                            docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                        }
                        await updateDoc(docRef, {
                            notes: newNotes,
                            updatedAt: serverTimestamp()
                        });
                        newNotesById.set(contact.id, newNotes);
                        if (contact.placeId) newNotesById.set(contact.placeId, newNotes);
                    })
                );
            } else {
                // En modo normal, usar updateAgendaItem
                await Promise.all(
                    Array.from(selectedContacts).map(placeId => {
                        const currentItem = items.find(item => item.placeId === placeId);
                        const currentNotes = currentItem?.notes || '';
                        const newNotes = currentNotes ? `${currentNotes}\n${bulkNotesText}` : bulkNotesText;
                        newNotesById.set(placeId, newNotes);
                        return updateAgendaItem(placeId, { notes: newNotes });
                    })
                );
            }
            setShowBulkNotesModal(false);
            setBulkNotesText('');
            setSelectedContacts(new Set());
            // Patch del caché: cada contacto con sus notas actualizadas.
            if (newNotesById.size > 0) {
                updateCachedContacts(user, agendas, (cachedItems) =>
                    cachedItems.map((item) => {
                        const notes =
                            newNotesById.get(item.id) ??
                            newNotesById.get(item.placeId);
                        return notes !== undefined ? { ...item, notes } : item;
                    })
                );
            }
            showToast('success', `Notas agregadas a ${count} contacto(s)`);
        } catch (err) {
            showToast('error', 'Error al agregar notas a los contactos');
        } finally {
            setBulkLoading(false);
            setBulkLoadingMessage('');
        }
    };

    const handleBulkWhatsApp = () => {
        if (selectedContacts.size === 0) return;

        // Filtrar contactos con teléfonos válidos
        const contactsWithPhone = Array.from(selectedContacts)
            .map(selectedId => displayItems.find(item => getItemId(item) === selectedId))
            .filter(item => item && item.phone && isValidWhatsappNumber(item.phone));

        if (contactsWithPhone.length === 0) {
            alert('Ninguno de los contactos seleccionados tiene un número de WhatsApp válido.');
            return;
        }

        if (contactsWithPhone.length < selectedContacts.size) {
            const invalid = selectedContacts.size - contactsWithPhone.length;
            if (!window.confirm(`${invalid} contacto(s) no tienen número válido y serán omitidos. ¿Continuar con ${contactsWithPhone.length}?`)) {
                return;
            }
        }

        setShowBulkWhatsAppModal(true);
    };

    const handleSendBulkWhatsApp = () => {
        if (!whatsappMessage.trim()) {
            alert('Por favor escribe un mensaje.');
            return;
        }

        const contactsWithPhone = Array.from(selectedContacts)
            .map(selectedId => displayItems.find(item => getItemId(item) === selectedId))
            .filter(item => item && item.phone && isValidWhatsappNumber(item.phone));

        const encodedMessage = encodeURIComponent(whatsappMessage);

        // Generar lista de contactos con enlaces
        const contactsList = contactsWithPhone.map(contact => ({
            name: contact.name,
            phone: contact.phone,
            placeId: contact.placeId || contact.id, // Usar id como fallback
            url: `https://wa.me/${formatWhatsappNumber(contact.phone)}?text=${encodedMessage}`
        }));

        setWhatsappContacts(contactsList);
        setSentContacts(new Set());
        setShowBulkWhatsAppModal(false);
        setShowWhatsappLinksModal(true);
    };

    const handleMarkAsSent = (placeId) => {
        setSentContacts(prev => {
            const newSet = new Set(prev);
            newSet.add(placeId);
            return newSet;
        });
    };

    const handleCloseWhatsappLinks = async () => {
        // Guardar el estado de WhatsApp no disponible en Firestore
        if (whatsappNotAvailable.size > 0) {
            setBulkLoading(true);
            setBulkLoadingMessage(`Actualizando estado de WhatsApp de ${whatsappNotAvailable.size} contacto(s)...`);
            try {
                if (selectedAgendaId === 'all') {
                    // En modo "all", actualizar cada contacto en su agenda correspondiente
                    await Promise.all(
                        Array.from(whatsappNotAvailable).map(async (selectedId) => {
                            const contact = allAgendaItems.find(item =>
                                getItemId(item) === selectedId
                            );
                            if (!contact) return;

                            let docRef;
                            if (contact.agendaId === 'main') {
                                docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                            } else {
                                docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                            }
                            await updateDoc(docRef, {
                                whatsappNotAvailable: true,
                                updatedAt: serverTimestamp()
                            });
                        })
                    );
                } else {
                    // En modo normal, usar updateAgendaItem
                    await Promise.all(
                        Array.from(whatsappNotAvailable).map(placeId =>
                            updateAgendaItem(placeId, { whatsappNotAvailable: true })
                        )
                    );
                }
                // Patcheamos el caché (si está cargado) con la bandera.
                patchCachedContacts(
                    user,
                    agendas,
                    Array.from(whatsappNotAvailable),
                    { whatsappNotAvailable: true }
                );
            } catch (err) {
                showToast('error', 'Error al actualizar contactos sin WhatsApp');
            } finally {
                setBulkLoading(false);
                setBulkLoadingMessage('');
            }
        }

        setShowWhatsappLinksModal(false);
        setWhatsappMessage('');
        setWhatsappContacts([]);
        setSentContacts(new Set());
        setWhatsappNotAvailable(new Set());
        setSelectedContacts(new Set());
    };

    const handleCreateStatus = async (fromModal = false) => {
        if (!newStatusName.trim()) {
            alert('Por favor ingresa un nombre para el estado');
            return;
        }
        try {
            const statusId = await createStatus(newStatusName, newStatusColor, newStatusEmoji);
            if (fromModal) {
                setBulkStatus(statusId);
            }
            setNewStatusName('');
            setNewStatusColor('#007bff');
            setNewStatusEmoji('📌');
            alert('Estado creado exitosamente');
        } catch (err) {
            alert('Error creando estado: ' + err.message);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleDeleteStatus = async (statusId) => {
        const confirmDelete = window.confirm('¿Estás seguro de que deseas eliminar este estado? Los contactos con este estado quedarán como "pendiente".');
        if (!confirmDelete) return;
        setBulkLoading(true);
        setBulkLoadingMessage('Eliminando estado y actualizando contactos...');
        try {
            await deleteStatus(statusId);
            // Actualizar contactos que tengan este estado a 'pendiente'
            const itemsWithStatus = items.filter(item => item.status === statusId);
            await Promise.all(
                itemsWithStatus.map(item => updateAgendaItem(item.placeId, { status: 'pendiente' }))
            );
        } catch (err) {
            alert('Error eliminando estado: ' + err.message);
        } finally {
            setBulkLoading(false);
            setBulkLoadingMessage('');
        }
    };

    const handleCreateAgenda = async () => {
        if (!newAgendaName.trim()) return;
        await createAgenda(newAgendaName);
        setNewAgendaName('');
        setShowCreateAgenda(false);
    };

    const handleEditAgendaName = async () => {
        if (!editingAgendaName.trim() || !editingAgendaId) return;
        await updateAgendaName(editingAgendaId, editingAgendaName);
        setEditingAgendaId(null);
        setEditingAgendaName('');
    };

    const showToast = (type, message, duration = 3000) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), duration);
    };

    const handleCreateManualContact = async () => {
        setCreateContactLoading(true);
        try {
            await createManualContact(newContact, 'manual');
            // Creación: no conocemos el shape completo (placeId autogenerado,
            // timestamps de servidor). Invalidamos para que el próximo acceso
            // a "all" traiga datos frescos una sola vez.
            invalidateAllContactsCache();
            setNewContact({
                name: '',
                address: '',
                phone: '',
                website: '',
                email: '',
                city: '',
                category: '',
                notes: '',
                status: 'pendiente',
            });
            setShowCreateContact(false);
            await updateCounts();
            showToast('success', 'Contacto creado exitosamente');
        } catch (err) {
            if (err.code === 'DUPLICATE_PHONE') {
                showToast('error', err.message);
            } else if (err.code === 'LIMIT_REACHED') {
                showToast('warning', err.message);
            } else {
                showToast('error', 'Error creando contacto: ' + err.message);
            }
        } finally {
            setCreateContactLoading(false);
        }
    };

    const handleExport = () => {
        if (plan !== 'premium') {
            alert('⭐ Exportar a CSV es una función Premium. Hazte Premium para desbloquearla.');
            return;
        }
        setExportLoading(true);
        try {
            // Generar CSV solo de los items filtrados
            const headers = ['Nombre', 'Dirección', 'Teléfono', 'Website', 'Email', 'Notas', 'Estado'];
            const rows = filteredItems.map((item) => [
                `"${(item.name || '').replace(/"/g, '""')}"`,
                `"${(item.address || '').replace(/"/g, '""')}"`,
                `"${(item.phone || '').replace(/"/g, '""')}"`,
                `"${(item.website || '').replace(/"/g, '""')}"`,
                `"${sanitizeEmail(item.email).replace(/"/g, '""')}"`,
                `"${(item.notes || '').replace(/"/g, '""')}"`,
                `"${item.status || 'pendiente'}"`,
            ]);
            const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
            downloadCsv(csv, `agenda-${selectedAgendaId}-filtrados.csv`);
        } catch (err) {
            alert('No se pudo exportar la agenda.');
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportAll = async () => {
        if (plan !== 'premium') {
            alert('⭐ Exportar a CSV es una función Premium. Hazte Premium para desbloquearla.');
            return;
        }
        setExportLoading(true);
        try {
            // Reutilizamos lo que ya tengamos cargado en memoria/caché; solo
            // pegamos a Firestore si realmente es necesario.
            const allContacts =
                (selectedAgendaId === 'all' && allAgendaItems.length > 0)
                    ? allAgendaItems
                    : await getAllContactsCachedOrFetch(user, agendas);

            const headers = ['Agenda', 'Nombre', 'Dirección', 'Teléfono', 'Website', 'Email', 'Notas', 'Estado'];
            const rows = allContacts.map((item) => [
                `"${(item.agendaName || '').replace(/"/g, '""')}"`,
                `"${(item.name || '').replace(/"/g, '""')}"`,
                `"${(item.address || '').replace(/"/g, '""')}"`,
                `"${(item.phone || '').replace(/"/g, '""')}"`,
                `"${(item.website || '').replace(/"/g, '""')}"`,
                `"${sanitizeEmail(item.email).replace(/"/g, '""')}"`,
                `"${(item.notes || '').replace(/"/g, '""')}"`,
                `"${item.status || 'pendiente'}"`,
            ]);
            const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
            downloadCsv(csv, `todas-las-agendas-completo.csv`);
        } catch (err) {
            alert('No se pudo exportar todas las agendas.');
        } finally {
            setExportLoading(false);
        }
    };

    const handleCopyEmails = async () => {
        if (plan !== 'premium') {
            alert('⭐ Copiar emails es una función Premium. Hazte Premium para desbloquearla.');
            return;
        }
        const emails = filteredItems
            .map(item => sanitizeEmail(item.email))
            .filter(email => email !== '');
        if (emails.length === 0) {
            alert('No hay emails disponibles para copiar.');
            return;
        }
        const emailText = emails.join(', ');
        try {
            await navigator.clipboard.writeText(emailText);
            alert(`${emails.length} emails filtrados copiados al portapapeles!`);
        } catch (err) {
            alert('Error al copiar emails.');
        }
    };

    const handleCopyAllEmails = async () => {
        if (plan !== 'premium') {
            alert('⭐ Copiar emails es una función Premium. Hazte Premium para desbloquearla.');
            return;
        }

        setExportLoading(true);
        try {
            // Mismo principio que handleExportAll: caché primero, peticiones solo si hace falta.
            const allContacts =
                (selectedAgendaId === 'all' && allAgendaItems.length > 0)
                    ? allAgendaItems
                    : await getAllContactsCachedOrFetch(user, agendas);

            const allEmails = allContacts
                .map((item) => sanitizeEmail(item.email))
                .filter((email) => email !== '');

            const uniqueEmails = [...new Set(allEmails)];

            if (uniqueEmails.length === 0) {
                alert('No hay emails disponibles para copiar.');
                return;
            }

            const emailText = uniqueEmails.join(', ');
            await navigator.clipboard.writeText(emailText);
            alert(`${uniqueEmails.length} emails únicos (de todas las agendas) copiados al portapapeles!`);
        } catch (err) {
            alert('Error al copiar emails.');
        } finally {
            setExportLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        handleExport,
        handleCopyEmails,
        handleExportAll,
        handleCopyAllEmails,
    }));

    const openNotesModal = (item) => {
        setNotesModalItemId(item.placeId);
        setNotesModalText(item.notes || '');
        setNotesModalTitle(item.name || '');
        setShowNotesModal(true);
    };

    const closeNotesModal = () => {
        setShowNotesModal(false);
        setNotesModalItemId(null);
        setNotesModalText('');
        setNotesModalTitle('');
    };

    const saveNotesModal = async () => {
        if (!notesModalItemId) return;
        try {
            if (selectedAgendaId === 'all') {
                const contactIdx = allAgendaItems.findIndex(item => (item.placeId || item.id) === notesModalItemId);
                if (contactIdx !== -1) {
                    const contact = allAgendaItems[contactIdx];
                    let docRef;
                    if (contact.agendaId === 'main') {
                        docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                    } else {
                        docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                    }
                    await updateDoc(docRef, {
                        notes: notesModalText,
                        updatedAt: serverTimestamp()
                    });
                    // Actualizar localmente para feedback instantáneo
                    setAllAgendaItems(prev => {
                        const updated = [...prev];
                        updated[contactIdx] = { ...contact, notes: notesModalText };
                        return updated;
                    });
                    patchCachedContacts(
                        user,
                        agendas,
                        [contact.placeId || contact.id].filter(Boolean),
                        { notes: notesModalText }
                    );
                }
            } else {
                await updateAgendaItem(notesModalItemId, { notes: notesModalText });
            }
            setFlashEffect({ placeId: notesModalItemId, type: 'save' });
            setTimeout(() => {
                setFlashEffect({ placeId: null, type: null });
            }, 1000);
            closeNotesModal();
        } catch (err) {
            alert('Error guardando notas');
        }
    };

    const saveAllEdited = async () => {
        const ids = Object.keys(editedItems || {});
        if (ids.length === 0) return;

        const editedContactId = editingRowId; // Guardar antes de limpiar
        if (!editedContactId) return;

        try {
            setSaveLoading(true);

            // Mapa id → partial, para patchear el caché con todos los cambios
            // en una sola operación al final.
            const patchesById = new Map();

            if (selectedAgendaId === 'all') {
                await Promise.all(ids.map(async (id) => {
                    const contactIdx = allAgendaItems.findIndex(item => (item.placeId || item.id) === id);
                    if (contactIdx === -1) return;
                    const contact = allAgendaItems[contactIdx];
                    let docRef;
                    if (contact.agendaId === 'main') {
                        docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                    } else {
                        docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                    }
                    await updateDoc(docRef, {
                        ...editedItems[id],
                        updatedAt: serverTimestamp()
                    });
                    // Actualizar localmente para feedback instantáneo
                    setAllAgendaItems(prev => {
                        const updated = [...prev];
                        updated[contactIdx] = { ...contact, ...editedItems[id] };
                        return updated;
                    });
                    patchesById.set(contact.id, editedItems[id]);
                    if (contact.placeId) patchesById.set(contact.placeId, editedItems[id]);
                }));
            } else {
                await Promise.all(ids.map(async (id) => {
                    await updateAgendaItem(id, editedItems[id]);
                    patchesById.set(id, editedItems[id]);
                }));
            }

            if (patchesById.size > 0) {
                updateCachedContacts(user, agendas, (cachedItems) =>
                    cachedItems.map((item) => {
                        const partial =
                            patchesById.get(item.id) ??
                            patchesById.get(item.placeId);
                        return partial ? { ...item, ...partial } : item;
                    })
                );
            }
            
            // Limpiar estados de edición
            setEditedItems({});
            setEditingRowId(null);

            // Activar efecto flash verde - esto previene el reordenamiento mientras se muestra
            setFlashEffect({ placeId: editedContactId, type: 'save' });
            
            // Desactivar efecto flash y descongelar orden después de 1.2 segundos
            setTimeout(() => {
                setFlashEffect({ placeId: null, type: null });
                setFrozenOrder(null);
            }, 1200);
        } catch (err) {
            alert('Hubo un error al guardar algunos cambios.');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleEditRow = (placeId) => {
        setEditingRowId(placeId);
    };

    const handleCancelEdit = () => {
        const canceledContactId = editingRowId; // Guardar antes de limpiar
        if (!canceledContactId) return;

        // Limpiar estados de edición
        setEditingRowId(null);
        setEditedItems({});

        // Activar efecto flash rojo de cancelación - esto previene el reordenamiento mientras se muestra
        setFlashEffect({ placeId: canceledContactId, type: 'cancel' });
        
        // Desactivar efecto flash y descongelar orden después de 1.2 segundos
        setTimeout(() => {
            setFlashEffect({ placeId: null, type: null });
            setFrozenOrder(null);
        }, 1200);
    };

    // Cambiar estado directamente sin modo edición
    const handleDirectStatusChange = async (placeId, newStatus) => {
        if (newStatus === '__MANAGE_STATUS__') {
            setPendingStatusContactId(placeId);
            setShowManageStatusModal(true);
            return;
        }

        setSaveLoading(true);
        try {
            if (selectedAgendaId === 'all') {
                const contactIdx = allAgendaItems.findIndex(item => (item.placeId || item.id) === placeId);
                if (contactIdx !== -1) {
                    const contact = allAgendaItems[contactIdx];
                    let docRef;
                    if (contact.agendaId === 'main') {
                        docRef = doc(db, 'users', user.uid, 'agenda', contact.placeId || contact.id);
                    } else {
                        docRef = doc(db, 'users', user.uid, 'agendas', contact.agendaId, 'contacts', contact.placeId || contact.id);
                    }
                    await updateDoc(docRef, {
                        status: newStatus,
                        updatedAt: serverTimestamp()
                    });
                    // Actualizar localmente para feedback instantáneo
                    setAllAgendaItems(prev => {
                        const updated = [...prev];
                        updated[contactIdx] = { ...contact, status: newStatus };
                        return updated;
                    });
                    patchCachedContacts(
                        user,
                        agendas,
                        [contact.placeId || contact.id].filter(Boolean),
                        { status: newStatus }
                    );
                }
            } else {
                await updateAgendaItem(placeId, { status: newStatus });
            }
            setFlashEffect({ placeId, type: 'save' });
            setTimeout(() => {
                setFlashEffect({ placeId: null, type: null });
            }, 1000);
        } catch (err) {
            alert('Error actualizando estado: ' + err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    // Crear estado desde el modal de administración
    const handleCreateStatusFromModal = async () => {
        if (!newStatusName.trim()) {
            alert('Por favor ingresa un nombre para el estado');
            return;
        }
        try {
            const statusId = await createStatus(newStatusName, newStatusColor, newStatusEmoji);
            setNewStatusName('');
            setNewStatusColor('#007bff');
            setNewStatusEmoji('📌');

            // Si hay un contacto pendiente, aplicar el nuevo estado
            if (pendingStatusContactId) {
                await handleDirectStatusChange(pendingStatusContactId, statusId);
                setPendingStatusContactId(null);
            }

            setShowManageStatusModal(false);
            alert('Estado creado exitosamente');
        } catch (err) {
            alert('Error creando estado: ' + err.message);
        }
    };

    // Eliminar estado desde el modal de administración
    const handleDeleteStatusFromModal = async (statusId) => {
        const statusObj = getStatusById(statusId);
        const confirmDelete = window.confirm(`¿Seguro quieres eliminar este estado?\n\n"${statusObj.name}"\n\nLos contactos con este estado quedarán como "pendiente".`);
        if (!confirmDelete) return;

        setSaveLoading(true);
        try {
            await deleteStatus(statusId);
            // Actualizar contactos que tengan este estado a 'pendiente'
            const itemsWithStatus = items.filter(item => item.status === statusId);
            await Promise.all(
                itemsWithStatus.map(item => updateAgendaItem(item.placeId, { status: 'pendiente' }))
            );
            alert('Estado eliminado exitosamente');
        } catch (err) {
            alert('Error eliminando estado: ' + err.message);
        } finally {
            setSaveLoading(false);
        }
    };



    if (!user) {
        return (
            <div>
                <h1>Mi Agenda</h1>
                <p>Inicia sesión con Google para guardar y gestionar tu agenda de contactos.</p>
                <button className="btn-primary" onClick={signInWithGoogle}>Iniciar sesión con Google</button>
            </div>
        );
    }

    if (!user) {
        return (
            <div>
                <p>Inicia sesión con Google para guardar y gestionar tus agendas de contactos.</p>
                <button className="btn-primary" onClick={signInWithGoogle}>Iniciar sesión con Google</button>
            </div>
        );
    }

    const selectedAgenda = agendas.find(a => a.id === selectedAgendaId);

    return (
        <>
            <div className="fade-in page-content">
                {toast && (
                    <div className={`toast-notification toast-${toast.type}`}>
                        <span className="toast-icon">
                            {toast.type === 'success' && '✓'}
                            {toast.type === 'error' && '✕'}
                            {toast.type === 'warning' && '⚠'}
                            {toast.type === 'info' && 'ℹ'}
                        </span>
                        <span className="toast-message">{toast.message}</span>
                    </div>
                )}
                {(loading || bulkLoading || exportLoading || allContactsLoading) && (
                    <div className="loading-overlay">
                        <div className="spinner"></div>
                        <p>
                            {bulkLoadingMessage ||
                                (exportLoading ? 'Procesando exportación...' : '') ||
                                (allContactsLoading ? 'Cargando todos los contactos...' : '') ||
                                'Cargando datos...'}
                        </p>
                    </div>
                )}

                <div className="agenda-header-compact">
                    <div className="agenda-selector-compact">
                        <label>Agenda: </label>
                        <select value={selectedAgendaId} onChange={(e) => {
                            if (e.target.value === 'create') {
                                setShowCreateAgenda(true);
                                e.target.value = selectedAgendaId; // reset select
                            } else {
                                setSelectedAgendaId(e.target.value);
                                setCurrentPage(1);
                                setSelectedContacts(new Set());
                            }
                        }}>
                            <option value="all">🌍 Todas las Agendas ({Object.values(counts).reduce((a, b) => a + b, 0)})</option>
                            {agendas.map((agenda) => (
                                <option key={agenda.id} value={agenda.id}>{agenda.name} ({counts[agenda.id] || 0})</option>
                            ))}
                            <option value="create">➕ Crear Nueva Agenda</option>
                        </select>
                    </div>
                    <div className="agenda-actions-compact">
                        {selectedAgendaId !== 'main' && selectedAgendaId !== 'all' && (
                            <button className="btn-secondary" onClick={() => { setEditingAgendaId(selectedAgendaId); setEditingAgendaName(selectedAgenda?.name || ''); }}>Editar Nombre</button>
                        )}
                        <button className="btn-secondary btn-create-contact" onClick={() => setShowCreateContact((prev) => !prev)} title="Crear un contacto">
                            {showCreateContact ? 'Cancelar' : '👤➕'}
                        </button>
                    </div>
                </div>

                {showCreateAgenda && (
                    <div className="agenda-section">
                        <input
                            type="text"
                            placeholder="Nombre de la nueva agenda"
                            value={newAgendaName}
                            onChange={(e) => setNewAgendaName(e.target.value)}
                        />
                        <div className="form-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowCreateAgenda(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleCreateAgenda}
                                disabled={!newAgendaName.trim()}
                            >
                                Crear
                            </button>
                        </div>
                    </div>
                )}

                {editingAgendaId && (
                    <div className="agenda-section">
                        <input
                            type="text"
                            value={editingAgendaName}
                            onChange={(e) => setEditingAgendaName(e.target.value)}
                        />
                        <div className="form-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => setEditingAgendaId(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleEditAgendaName}
                                disabled={!editingAgendaName.trim()}
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                )}

                {showCreateContact && (
                    <div className="form-section">
                        <h3>Crear Contacto Manualmente</h3>
                        <input type="text" placeholder="Nombre" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} />
                        <input type="text" placeholder="Dirección" value={newContact.address} onChange={(e) => setNewContact({ ...newContact, address: e.target.value })} />
                        <input type="text" placeholder="Teléfono" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} />
                        <input type="text" placeholder="Website" value={newContact.website} onChange={(e) => setNewContact({ ...newContact, website: e.target.value })} />
                        <input type="email" placeholder="Email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                        <input type="text" placeholder="Ciudad" value={newContact.city} onChange={(e) => setNewContact({ ...newContact, city: e.target.value })} />
                        <input type="text" placeholder="Categoría" value={newContact.category} onChange={(e) => setNewContact({ ...newContact, category: e.target.value })} />
                        <textarea placeholder="Notas" value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} />
                        <select value={newContact.status} onChange={(e) => setNewContact({ ...newContact, status: e.target.value })}>
                            {allStatuses.map(status => (
                                <option key={status.id} value={status.id}>
                                    {status.emoji} {status.name}
                                </option>
                            ))}
                        </select>
                        <div className="form-actions">
                            <button
                                className="btn-primary"
                                onClick={handleCreateManualContact}
                                disabled={createContactLoading}
                            >
                                {createContactLoading ? 'Creando...' : 'Crear'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Plan info moved to top left */}
                {!canAddMore && (
                    <p className="freemium-warning">
                        Has alcanzado el máximo de {maxFree} contactos en esta agenda FREE.
                    </p>
                )}

                <div className="dashboard-filters-row">
                    {/* Input de búsqueda movido arriba */}
                    <div className="filter-item">
                        <label className="filter-label">Ciudad</label>
                        <select value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setCurrentPage(1); setSelectedContacts(new Set()); }}>
                            <option value="all">Todas ({displayItems.length})</option>
                            {dynamicFilterOptions?.cities?.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label className="filter-label">Categoría</label>
                        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); setSelectedContacts(new Set()); }}>
                            <option value="all">Todas ({displayItems.length})</option>
                            {dynamicFilterOptions?.categories?.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label className="filter-label">Estado</label>
                        <select value={statusFilter} onChange={(e) => {
                            if (e.target.value === "__MANAGE_STATUS__") {
                                setShowManageStatusModal(true);
                                // No cambiar el filtro
                                e.target.value = statusFilter;
                                return;
                            }
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                            setSelectedContacts(new Set());
                        }}>
                            <option value="all">Todos ({displayItems.length})</option>
                            {allStatuses.map((status) => {
                                const count = displayItems.filter(item => (item.status || 'pendiente') === status.id).length;
                                return (
                                    <option key={status.id} value={status.id}>
                                        {status.emoji} {status.name} ({count})
                                    </option>
                                );
                            })}
                            <option value="__MANAGE_STATUS__">⚙️ Administrar Estados</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label className="filter-label">Ordenar por</label>
                        <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); setSelectedContacts(new Set()); }}>
                            <option value="whatsapp-available">💬 WhatsApp Disponible</option>
                            <option value="complete-desc">📊 Más Completos</option>
                            <option value="complete-asc">📉 Menos Completos</option>
                            <option value="city">🏙️ Ciudad</option>
                            <option value="date-newest">📅 Más Recientes</option>
                            <option value="date-oldest">📅 Más Antiguos</option>
                        </select>
                    </div>
                    <div className="filter-item filter-item-checkbox-group">
                        <label className="filter-label">Filtrar solo contactos con:</label>
                        <div className="checkboxes-container">
                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={fieldFilters.whatsapp}
                                    onChange={(e) => {
                                        setFieldFilters(prev => ({ ...prev, whatsapp: e.target.checked }));
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                />
                                <span>💬 WhatsApp</span>
                            </label>
                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={fieldFilters.phone}
                                    onChange={(e) => {
                                        setFieldFilters(prev => ({ ...prev, phone: e.target.checked }));
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                />
                                <span>📞 Teléfono</span>
                            </label>
                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={fieldFilters.email}
                                    onChange={(e) => {
                                        setFieldFilters(prev => ({ ...prev, email: e.target.checked }));
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                />
                                <span>✉️ Email</span>
                            </label>
                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={fieldFilters.website}
                                    onChange={(e) => {
                                        setFieldFilters(prev => ({ ...prev, website: e.target.checked }));
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                />
                                <span>🌐 Website</span>
                            </label>
                            <label
                                className="checkbox-item"
                                title="Contactos que comparten teléfono, email o nombre normalizado con otros"
                            >
                                <input
                                    type="checkbox"
                                    checked={fieldFilters.duplicates}
                                    onChange={(e) => {
                                        setFieldFilters(prev => ({ ...prev, duplicates: e.target.checked }));
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                />
                                <span>
                                    ⚠️ Duplicados
                                    {duplicatesCount > 0 && (
                                        <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                                            ({duplicatesCount})
                                        </span>
                                    )}
                                </span>
                            </label>
                            {(fieldFilters.whatsapp || fieldFilters.phone || fieldFilters.email || fieldFilters.website || fieldFilters.duplicates) && (
                                <button
                                    className="btn-clear-checkboxes"
                                    onClick={() => {
                                        setFieldFilters({ whatsapp: false, phone: false, email: false, website: false, duplicates: false });
                                        setCurrentPage(1);
                                        setSelectedContacts(new Set());
                                    }}
                                >
                                    ✕ Limpiar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="global-search-container">
                    <label className="filter-label">Buscar en todas las agendas</label>
                    <input
                        type="text"
                        className="search-highlighted"
                        placeholder="Nombre, email, teléfono, dirección..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); setSelectedContacts(new Set()); setSelectedAgendaId('all'); }}
                    />
                </div>
                <div className="table-info-row">
                    <p className="table-info">
                        {filteredItems.length} contactos
                    </p>
                    <div className="column-visibility">
                        <button
                            type="button"
                            className="btn-secondary btn-column-toggle"
                            onClick={() => setShowColumnMenu((prev) => !prev)}
                            title={showColumnMenu ? 'Cerrar editor de columnas' : 'Abrir editor de columnas'}
                        >
                            {showColumnMenu ? '❌' : '👁️'}
                        </button>
                        {showColumnMenu && (
                            <div className={`column-visibility-menu ${selectedAgendaId === 'all' ? 'columns-all' : 'columns-single'}`}>
                                <div className="column-visibility-actions">
                                    <button type="button" className="column-visibility-reset" onClick={resetColumnVisibility}>
                                        Restablecer
                                    </button>
                                </div>
                                <div className="column-visibility-quick-view">
                                    <label className="column-visibility-quick-view-label">
                                        <input
                                            type="checkbox"
                                            checked={isQuickViewActive}
                                            onChange={toggleQuickView}
                                        />
                                        <span>Vista rápida: Solo Nombre y Teléfono</span>
                                    </label>
                                </div>
                                <div className="column-visibility-grid">
                                    {columnOptions.map((column) => (
                                        <label
                                            key={column.id}
                                            className={`column-visibility-item ${column.disabled ? 'is-disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isColumnVisible(column.id)}
                                                onChange={() => toggleColumnVisibility(column.id)}
                                                disabled={column.disabled}
                                            />
                                            <span>{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {selectedContacts.size > 0 && (
                    <>
                        <div className="bulk-actions-container">
                            <div className="bulk-actions-buttons">
                                <div className="bulk-move-group">
                                <label htmlFor="bulk-move-select">Mover a:</label>
                                <select
                                    id="bulk-move-select"
                                    className="bulk-move-select"
                                    onChange={(e) => { handleMoveSelected(e.target.value); e.target.value = ''; }}
                                    disabled={bulkLoading}
                                >
                                    <option value="">Seleccionar agenda</option>
                                    {agendas.filter(a => a.id !== selectedAgendaId).map((agenda) => (
                                        <option key={agenda.id} value={agenda.id}>{agenda.name} ({counts[agenda.id] || 0})</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                className="btn-bulk btn-bulk-delete"
                                onClick={handleDeleteSelected}
                                title="Eliminar seleccionados"
                                disabled={bulkLoading}
                            >
                                🗑️ Eliminar
                            </button>
                            <button
                                className="btn-bulk btn-bulk-whatsapp"
                                onClick={handleBulkWhatsApp}
                                title="Enviar WhatsApp masivo"
                                disabled={bulkLoading}
                            >
                                💬 WhatsApp Masivo
                            </button>
                            <button
                                className="btn-bulk btn-bulk-notes"
                                onClick={() => setShowBulkNotesModal(true)}
                                title="Agregar notas"
                                disabled={bulkLoading}
                            >
                                📝 Agregar Notas
                            </button>
                            <button
                                className="btn-bulk btn-bulk-status"
                                onClick={() => setShowBulkStatusModal(true)}
                                title="Cambiar estado"
                                disabled={bulkLoading}
                            >
                                🔄 Cambiar Estado
                            </button>
                            <button className="bulk-actions-close" onClick={handleCloseBulkActions} title="Cerrar y deseleccionar todo">
                                ✕
                            </button>
                        </div>
                    </div>
                    <div className="bulk-actions-count-wrapper">
                        <span className="bulk-actions-count">({selectedContacts.size}) seleccionados</span>
                    </div>
                    </>
                )}

                <div className="table-container">
                    <table className="results-table">
                        <thead>
                            <tr>
                                {selectedAgendaId === 'all' && (
                                    <th className={isColumnVisible('agenda') ? '' : 'column-hidden'}>Agenda</th>
                                )}
                                <th className={isColumnVisible('name') ? '' : 'column-hidden'}>Nombre</th>
                                <th className={isColumnVisible('address') ? '' : 'column-hidden'}>Dirección</th>
                                <th className={isColumnVisible('phone') ? '' : 'column-hidden'}>Teléfono</th>
                                <th className={isColumnVisible('website') ? '' : 'column-hidden'}>Website</th>
                                <th className={isColumnVisible('email') ? '' : 'column-hidden'}>Email</th>
                                <th className={isColumnVisible('notes') ? '' : 'column-hidden'}>Notas</th>
                                <th className={isColumnVisible('status') ? '' : 'column-hidden'}>Estado</th>
                                <th className={isColumnVisible('acciones') ? '' : 'column-hidden'}>Acciones</th>
                                <th><input type="checkbox" className="checkbox-select" checked={paginatedItems.length > 0 && paginatedItems.every(item => selectedContacts.has(getItemId(item)))} onChange={handleSelectAll} /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedItems.map((item) => {
                                const itemId = getItemId(item);
                                const isEditing = editingRowId === itemId;
                                const isDeleting = deletingContacts.has(itemId);
                                const isMoving = movingContacts.has(itemId);
                                const isSaveFlash = flashEffect.placeId === itemId && flashEffect.type === 'save';
                                const isCancelFlash = flashEffect.placeId === itemId && flashEffect.type === 'cancel';
                                const duplicateInfo = duplicatesMap.get(itemId);
                                const duplicateTitle = duplicateInfo
                                    ? `${describeDuplicateReasons(duplicateInfo.reasons)} — ${duplicateInfo.matchIds.size} coincidencia${duplicateInfo.matchIds.size === 1 ? '' : 's'}`
                                    : '';
                                const displayName = editedItems[itemId]?.hasOwnProperty('name') ? editedItems[itemId].name : (item.name || '-');
                                const displayPhone = editedItems[itemId]?.hasOwnProperty('phone') ? editedItems[itemId].phone : (item.phone || '-');
                                const displayWebsite = editedItems[itemId]?.hasOwnProperty('website') ? editedItems[itemId].website : (item.website || '-');
                                const rawEmail = editedItems[itemId]?.hasOwnProperty('email') ? editedItems[itemId].email : item.email;
                                const cleanEmail = sanitizeEmail(rawEmail);
                                const displayEmail = cleanEmail || '-';
                                const displayStatus = editedItems[itemId]?.status || item.status || 'pendiente';

                                return (
                                    <tr
                                        key={itemId}
                                        className={`agenda-row ${isEditing ? 'row-editing' : ''} ${isDeleting ? 'row-deleting' : ''} ${isMoving ? 'row-moving' : ''} ${isSaveFlash ? 'row-flash-save' : ''} ${isCancelFlash ? 'row-flash-cancel' : ''}`}
                                    >
                                        {selectedAgendaId === 'all' && (
                                            <td className={isColumnVisible('agenda') ? '' : 'column-hidden'}>
                                                <span className="agenda-badge">{item.agendaName || '-'}</span>
                                            </td>
                                        )}
                                        <td className={isColumnVisible('name') ? '' : 'column-hidden'}>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    className="edit-input"
                                                    value={displayName}
                                                    onChange={(e) => setEditedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], name: e.target.value } }))}
                                                />
                                            ) : (
                                                <span className="cell-text name-with-badge" title={displayName}>
                                                    {duplicateInfo && (
                                                        <span
                                                            className="duplicate-badge"
                                                            title={duplicateTitle}
                                                            aria-label={duplicateTitle}
                                                        >
                                                            ⚠️
                                                        </span>
                                                    )}
                                                    {displayName}
                                                </span>
                                            )}
                                        </td>
                                        <td className={isColumnVisible('address') ? '' : 'column-hidden'}>
                                            <div className="address-cell">
                                                <span className="cell-text" title={item.address}>{item.address ? (item.address.length > 30 ? item.address.slice(0, 30) + '…' : item.address) : '-'}</span>
                                                {item.address && (
                                                    <a
                                                        className="maps-link"
                                                        href={`https://maps.google.com/?q=${encodeURIComponent(item.address)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Abrir en Google Maps"
                                                    >
                                                        📍
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`phone-cell ${isColumnVisible('phone') ? '' : 'column-hidden'}`}>
                                            <div className="phone-cell-inner">
                                                {isEditing ? (
                                                    <div className="phone-edit-container">
                                                        <input
                                                            type="text"
                                                            className="edit-input phone-input"
                                                            value={displayPhone}
                                                            title={displayPhone}
                                                            onChange={(e) => setEditedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], phone: e.target.value } }))}
                                                        />
                                                        {isValidWhatsappNumber(displayPhone) && (
                                                            <label className="whatsapp-edit-label" title="Marcar WhatsApp como no disponible">
                                                                <input
                                                                    type="checkbox"
                                                                    className="whatsapp-edit-checkbox"
                                                                    checked={editedItems[itemId]?.whatsappNotAvailable ?? item.whatsappNotAvailable ?? false}
                                                                    onChange={(e) => setEditedItems(prev => ({
                                                                        ...prev,
                                                                        [itemId]: {
                                                                            ...prev[itemId],
                                                                            whatsappNotAvailable: e.target.checked
                                                                        }
                                                                    }))}
                                                                />
                                                                <span className="whatsapp-edit-text">WA no disponible</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="cell-text" title={displayPhone}>{displayPhone ? (displayPhone.length > 5 ? displayPhone.slice(0, 5) + '…' : displayPhone) : '-'}</span>
                                                )}
                                                {displayPhone && displayPhone !== '-' && !isEditing && (
                                                    <a
                                                        className="phone-link"
                                                        href={`tel:${displayPhone}`}
                                                        title="Llamar"
                                                    >
                                                        📞
                                                    </a>
                                                )}
                                                {isValidWhatsappNumber(displayPhone) && !isEditing && (
                                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                                    <a
                                                        className={`whatsapp-link ${item.whatsappNotAvailable ? 'not-available' : ''}`}
                                                        href={item.whatsappNotAvailable ? undefined : `https://wa.me/${formatWhatsappNumber(displayPhone)}`}
                                                        target={item.whatsappNotAvailable ? '' : '_blank'}
                                                        rel="noopener noreferrer"
                                                        title={item.whatsappNotAvailable ? "WhatsApp no disponible" : "Enviar WhatsApp"}
                                                        onClick={(e) => {
                                                            if (item.whatsappNotAvailable) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    >
                                                        <img className="whatsapp-icon" src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className={isColumnVisible('website') ? '' : 'column-hidden'}>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    className="edit-input"
                                                    value={displayWebsite}
                                                    onChange={(e) => setEditedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], website: e.target.value } }))}
                                                />
                                            ) : (
                                                displayWebsite !== '-' ? (
                                                    <a href={displayWebsite} target="_blank" rel="noopener noreferrer" className="link-text" title={displayWebsite}>Visitar</a>
                                                ) : (
                                                    <span className="cell-text">-</span>
                                                )
                                            )}
                                        </td>
                                        <td className={isColumnVisible('email') ? '' : 'column-hidden'}>
                                            {isEditing ? (
                                                <input
                                                    type="email"
                                                    className="edit-input"
                                                    value={rawEmail || ''}
                                                    onChange={(e) => setEditedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], email: e.target.value } }))}
                                                />
                                            ) : (
                                                <span className="cell-text" title={displayEmail}>{displayEmail}</span>
                                            )}
                                        </td>
                                        <td className={isColumnVisible('notes') ? '' : 'column-hidden'}>
                                            <button
                                                className={`btn-secondary btn-notes ${((editedItems[itemId]?.notes || item.notes || '').trim()) ? 'btn-notes-filled' : ''}`}
                                                title={(editedItems[itemId]?.notes || item.notes || '').trim() || 'Sin notas'}
                                                onClick={() => openNotesModal(item)}
                                            >
                                                📝
                                            </button>
                                        </td>
                                        <td className={isColumnVisible('status') ? '' : 'column-hidden'}>
                                            <select
                                                className="status-select-direct"
                                                value={displayStatus}
                                                onChange={(e) => handleDirectStatusChange(itemId, e.target.value)}
                                                style={{
                                                    borderColor: getStatusById(displayStatus).color,
                                                    boxShadow: `0 1px 3px ${getStatusById(displayStatus).color}60`,
                                                    background: `linear-gradient(135deg, ${getStatusById(displayStatus).color}28 0%, ${getStatusById(displayStatus).color}12 100%)`
                                                }}
                                                disabled={saveLoading}
                                            >
                                                {allStatuses.map(status => (
                                                    <option key={status.id} value={status.id}>
                                                        {status.emoji} {status.name}
                                                    </option>
                                                ))}
                                                <option value="__MANAGE_STATUS__">⚙️ Administrar Estados</option>
                                            </select>
                                        </td>
                                        <td className={isColumnVisible('acciones') ? '' : 'column-hidden'}>
                                            {isEditing ? (
                                                <div className="action-buttons">
                                                    <button className="btn-icon btn-save" onClick={saveAllEdited} title="Guardar cambios">✓</button>
                                                    <button className="btn-icon btn-cancel" onClick={handleCancelEdit} title="Cancelar">✕</button>
                                                </div>
                                            ) : (
                                                <button className="btn-icon btn-edit" onClick={() => handleEditRow(itemId)} title="Editar contacto">✎</button>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="checkbox-select"
                                                checked={selectedContacts.has(itemId)}
                                                onChange={() => handleSelectContact(itemId)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                            {paginatedItems.length === 0 && (
                                <tr>
                                    <td colSpan={visibleColumnCount} className="empty-table-cell">
                                        No hay contactos para los filtros seleccionados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="pagination">
                        <button className="btn-secondary" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                            Anterior
                        </button>
                        <span>Página {currentPage} de {totalPages}</span>
                        <button className="btn-secondary" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            Siguiente
                        </button>
                    </div>
                )}

                {/* Selector de contactos por página al final */}
                <div className="items-per-page-footer">
                    <label className="filter-label">Contactos por página</label>
                    <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); setSelectedContacts(new Set()); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                {/* ...existing code... */}

                {showNotesModal && (
                    <div className="modal-overlay" onClick={closeNotesModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>Editar notas {notesModalTitle ? `- ${notesModalTitle}` : ''}</h2>
                            <textarea
                                value={notesModalText}
                                onChange={(e) => setNotesModalText(e.target.value)}
                                maxLength={MAX_NOTES_LENGTH}
                                style={{ minHeight: '220px' }}
                            />
                            <div className="notes-counter">{notesModalText.length} / {MAX_NOTES_LENGTH}</div>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={closeNotesModal}>Cancelar</button>
                                <button className="btn-primary" onClick={saveNotesModal}>Guardar</button>
                            </div>
                        </div>
                    </div>
                )}

                {showBulkStatusModal && (
                    <div className="modal-overlay" onClick={() => setShowBulkStatusModal(false)}>
                        <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Cambiar Estado Masivo</h2>
                            <p>Cambiar el estado de {selectedContacts.size} contacto(s) a:</p>

                            <button
                                className="btn-manage-status"
                                onClick={() => setShowManageStatusModal(true)}
                            >
                                ⚙️ Administrar Estados
                            </button>

                            <select
                                className="bulk-status-select"
                                value={bulkStatus}
                                onChange={(e) => setBulkStatus(e.target.value)}
                                style={{ borderColor: getStatusById(bulkStatus).color }}
                            >
                                {allStatuses.map(status => (
                                    <option key={status.id} value={status.id}>
                                        {status.emoji} {status.name}
                                    </option>
                                ))}
                            </select>

                            <div className="modal-actions modal-actions-equal">
                                <button className="btn-secondary" onClick={() => setShowBulkStatusModal(false)}>Cancelar</button>
                                <button className="btn-primary" onClick={handleBulkStatusChange}>Aplicar Cambio</button>
                            </div>
                        </div>
                    </div>
                )}

                {showBulkNotesModal && (
                    <div className="modal-overlay" onClick={() => setShowBulkNotesModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>Agregar Notas Masivas</h2>
                            <p>Agregar nota a {selectedContacts.size} contacto(s):</p>
                            <textarea
                                value={bulkNotesText}
                                onChange={(e) => setBulkNotesText(e.target.value)}
                                maxLength={MAX_NOTES_LENGTH}
                                placeholder="Escribe la nota que se agregará a todos los contactos seleccionados..."
                                style={{ minHeight: '180px' }}
                            />
                            <div className="notes-counter">{bulkNotesText.length} / {MAX_NOTES_LENGTH}</div>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => { setShowBulkNotesModal(false); setBulkNotesText(''); }}>Cancelar</button>
                                <button className="btn-primary" onClick={handleBulkNotesAdd}>Agregar</button>
                            </div>
                        </div>
                    </div>
                )}

                {showBulkWhatsAppModal && (
                    <div className="modal-overlay" onClick={() => setShowBulkWhatsAppModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2>💬 Envío Masivo por WhatsApp</h2>
                            <p>Enviar mensaje a {Array.from(selectedContacts).map(selectedId => displayItems.find(item => getItemId(item) === selectedId)).filter(item => item && item.phone && isValidWhatsappNumber(item.phone)).length} contacto(s) con número válido:</p>
                            <textarea
                                value={whatsappMessage}
                                onChange={(e) => setWhatsappMessage(e.target.value)}
                                placeholder="Escribe el mensaje que enviarás por WhatsApp...\n\nEjemplo:\nHola, soy [tu nombre] de [tu empresa]. ¿Te gustaría conocer nuestra oferta?"
                                style={{ minHeight: '220px' }}
                                maxLength={1000}
                            />
                            <div className="notes-counter">{whatsappMessage.length} / 1000</div>
                            <div className="whatsapp-info">
                                <p>ℹ️ Se generará una lista de enlaces para cada contacto.</p>
                                <p>✅ Podrás enviar los mensajes uno por uno con un solo click.</p>
                            </div>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => { setShowBulkWhatsAppModal(false); setWhatsappMessage(''); }}>Cancelar</button>
                                <button className="btn-primary" onClick={handleSendBulkWhatsApp}>Generar Enlaces</button>
                            </div>
                        </div>
                    </div>
                )}

                {showWhatsappLinksModal && (
                    <div className="modal-overlay" onClick={handleCloseWhatsappLinks}>
                        <div className="modal-content modal-whatsapp-links" onClick={(e) => e.stopPropagation()}>
                            <h2>💬 Enviar WhatsApp a Contactos</h2>
                            <p className="whatsapp-progress">
                                Enviados: {sentContacts.size} / {whatsappContacts.length}
                            </p>
                            <div className="whatsapp-message-preview">
                                <strong>Mensaje:</strong>
                                <p>{whatsappMessage}</p>
                            </div>
                            <div className="whatsapp-contacts-list">
                                {whatsappContacts.map((contact, index) => {
                                    const isSent = sentContacts.has(contact.placeId);
                                    const isNotAvailable = whatsappNotAvailable.has(contact.placeId);
                                    return (
                                        <div key={contact.placeId} className={`whatsapp-contact-item ${isSent ? 'sent' : ''} ${isNotAvailable ? 'not-available' : ''}`}>
                                            <div className="contact-info">
                                                <span className="contact-number">{index + 1}.</span>
                                                <span className="contact-name">{contact.name}</span>
                                                <span className="contact-phone">{contact.phone}</span>
                                            </div>
                                            <div className="contact-actions">
                                                <a
                                                    href={contact.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`btn-whatsapp-send ${isSent ? 'sent' : ''}`}
                                                    onClick={() => handleMarkAsSent(contact.placeId)}
                                                >
                                                    {isSent ? '✓ Enviado' : '📱 Enviar WhatsApp'}
                                                </a>
                                                <label className="whatsapp-unavailable-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={isNotAvailable}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setWhatsappNotAvailable(prev => new Set([...prev, contact.placeId]));
                                                            } else {
                                                                setWhatsappNotAvailable(prev => {
                                                                    const newSet = new Set(prev);
                                                                    newSet.delete(contact.placeId);
                                                                    return newSet;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span>No disponible</span>
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="modal-actions">
                                <button className="btn-primary" onClick={handleCloseWhatsappLinks}>
                                    {sentContacts.size === whatsappContacts.length ? 'Finalizar' : 'Cerrar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {Object.keys(editedItems || {}).length > 0 && ReactDOM.createPortal(
                <button
                    className="fab-save"
                    onClick={saveAllEdited}
                    disabled={saveLoading}
                >
                    {saveLoading ? 'Guardando…' : `Guardar cambios (${Object.keys(editedItems || {}).length})`}
                </button>,
                document.body
            )}

            {/* Modal de Administración de Estados */}
            {showManageStatusModal && (
                <div className="modal-overlay" onClick={() => setShowManageStatusModal(false)}>
                    <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
                        <h2>⚙️ Administrar Estados</h2>

                        {/* Sección: Estados Existentes */}
                        <div className="manage-status-section">
                            <h3>Estados Existentes</h3>
                            <div className="status-list">
                                {allStatuses.map(status => (
                                    <div key={status.id} className="status-item">
                                        <span
                                            className="status-badge"
                                            style={{ backgroundColor: status.color }}
                                        >
                                            {status.emoji} {status.name}
                                        </span>
                                        {status.id !== 'pendiente' && (
                                            <button
                                                className="btn-icon btn-delete-status"
                                                onClick={() => handleDeleteStatusFromModal(status.id)}
                                                title="Eliminar estado"
                                                disabled={saveLoading}
                                            >
                                                🗑️
                                            </button>
                                        )}
                                        {status.id === 'pendiente' && (
                                            <span className="status-default-badge">Predeterminado</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="divider"></div>

                        {/* Sección: Crear Nuevo Estado */}
                        <div className="manage-status-section">
                            <h3>Crear Nuevo Estado</h3>
                            <div className="inline-form-row">
                                <input
                                    type="text"
                                    placeholder="Nombre (ej: En Proceso)"
                                    value={newStatusName}
                                    onChange={(e) => setNewStatusName(e.target.value)}
                                    className="inline-status-input"
                                />
                                <select
                                    value={newStatusColor}
                                    onChange={(e) => setNewStatusColor(e.target.value)}
                                    className="inline-color-select"
                                >
                                    {AVAILABLE_COLORS.map(color => (
                                        <option key={color.value} value={color.value}>
                                            {color.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={newStatusEmoji}
                                    onChange={(e) => setNewStatusEmoji(e.target.value)}
                                    className="inline-emoji-select"
                                >
                                    <option value="📌">📌 Alfiler</option>
                                    <option value="⏳">⏳ Reloj</option>
                                    <option value="✓">✓ Check</option>
                                    <option value="📋">📋 Portapapeles</option>
                                    <option value="🔄">🔄 Reciclar</option>
                                    <option value="💰">💰 Dinero</option>
                                    <option value="❌">❌ Cruz</option>
                                    <option value="⏰">⏰ Alarma</option>
                                    <option value="🔔">🔔 Campana</option>
                                    <option value="📞">📞 Teléfono</option>
                                    <option value="✉️">✉️ Sobre</option>
                                    <option value="🎯">🎯 Diana</option>
                                    <option value="🚀">🚀 Cohete</option>
                                    <option value="⭐">⭐ Estrella</option>
                                    <option value="🔥">🔥 Fuego</option>
                                    <option value="💡">💡 Bombilla</option>
                                    <option value="📝">📝 Nota</option>
                                    <option value="📊">📊 Gráfico</option>
                                    <option value="🎉">🎉 Celebración</option>
                                    <option value="⚠️">⚠️ Advertencia</option>
                                </select>
                            </div>
                            <button
                                className="btn-create-status"
                                onClick={handleCreateStatusFromModal}
                                disabled={!newStatusName.trim() || saveLoading}
                            >
                                ➕ Crear Estado
                            </button>
                        </div>

                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => {
                                setShowManageStatusModal(false);
                                setNewStatusName('');
                                setNewStatusColor('#007bff');
                                setNewStatusEmoji('📌');
                                setPendingStatusContactId(null);
                            }}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
